import { Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IAutomatedCampaignCandidatEmailList, triggerAiAutomatedCampaign } from 'wisestep-api';
import { IUserDTO, WsMoreOptionButton } from 'wisestep-base';

import { useUserContext, useWorkspaceContext } from '../../../../context';
import CardWrapper from '../../../../features/automatedEmailCampaignSettings/childRoutes/CardWrapper';
import { copyStringToClipboard } from '../../../../features/candidateLayout/components/childComponets/CandidateDetailsCard/Helper';
import { CampaignEmailDrawer } from '../../../../features/emailSettings/components/campaignDrawer';
import { reOpenJob } from '../../../../features/jobs/helper';
import CloseJobDialogue from '../../../../features/jobs/listing/components/closeJobDialogue';
import { success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { useJobCRMContext } from '../../context';
import { defaultAISmartSourcePayload } from '../helper/JobsCrmHelper';

import JobCRMLeftHeader from './JobCRMLeftHeader';

const JobCRMHeader = () => {
  const { job, getJobDetails } = useJobCRMContext();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const navigate = useNavigate();
  const workspace = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openCloseDialog, setOpenCloseDialog] = useState<boolean>(false);
  const [campaignDrawer, setCamPaignDrawer] = useState<boolean>(false);
  const [finalizedCampaignData, setFinalizedCampaignData] =
    useState<IAutomatedCampaignCandidatEmailList>({} as IAutomatedCampaignCandidatEmailList);

  const isAssigned =
    loggedInUser?.email &&
    job?.assignedTo?.filter((user) => user.recruiterId === loggedInUser.email);

  const editJob = () => navigate(`/jobs/${job?.id}/edit`);
  const cloneJob = () => navigate(`/jobs/${job?.id}/clone`);
  const changeJobAttributes = () => navigate('/job-fields-settings');

  const copyClipBoard = (data: string) => {
    copyStringToClipboard(data);
    setNotification(success('Copied Successfully !!'));
  };

  const copyApplicationLink = () => {
    if (job?.jobCode) {
      const applicationLink = `${workspace?.link}-${job?.jobCode}@apply.wisestep-inc.com`;
      copyClipBoard(applicationLink);
    } else {
      setNotification(warning('Job Code is not available for this job.'));
    }
  };

  const closeJob = () => setOpenCloseDialog(true);

  const handleToggleDrawer = () => setCamPaignDrawer((prev) => !prev);

  const triggerAiEmailCampaign = async () => {
    if (job?.id) {
      try {
        const res = await triggerAiAutomatedCampaign(job?.id, defaultAISmartSourcePayload(job));
        setFinalizedCampaignData(res?.data);
        handleToggleDrawer();
      } catch (e: any) {
        console.error(e);
        if (e?.response?.status === 400) {
          setNotification({
            message: `${e?.response?.data}`,
            type: 'error',
          });
        } else {
          setNotification({
            message: 'Something went wrong!',
            type: 'error',
          });
        }
      }
    }
  };

  const handleReOpenJob = async () => {
    setNotification(await reOpenJob(job?.id ?? ''));
    getJobDetails();
  };

  const accessPermisson =
    job?.canManageJob || job?.createdBy === loggedInUser?.email || isEmpty(isAssigned);

  const finalizedOptions = [
    ...(accessPermisson ? ['Edit Job', 'Clone Job', 'Change Job Attributes'] : []),
    'Copy Application Link',
    ...(workspace?.owns ? ['Trigger AI Email Campaign'] : []),
    ...(accessPermisson ? [job?.isClosedForAssigningRecruiters ? 'Re-Open Job' : 'Close Job'] : []),
  ];

  const moreOptionHandlers = new Map<string, () => void>([
    ['Edit Job', () => editJob()],
    ['Clone Job', () => cloneJob()],
    ['Change Job Attributes', () => changeJobAttributes()],
    ['Copy Application Link', () => copyApplicationLink()],
    ['Trigger AI Email Campaign', () => triggerAiEmailCampaign()],
    ['Close Job', () => closeJob()],
    ['Re-Open Job', () => handleReOpenJob()],
  ]);

  const onMoreOptionClick = (option: string) => {
    const handler = moreOptionHandlers.get(option);
    if (!handler) {
      console.warn(`No handler found for option: ${option}`);
      return;
    }
    handler();
  };
  return (
    <>
      <CardWrapper sx={{ p: 2 }}>
        <Grid display="flex" justifyContent="space-between" alignItems="center">
          <JobCRMLeftHeader />
          <Grid item display="flex" alignItems="center" columnGap={1}>
            <WsMoreOptionButton
              onItemClick={(option) => onMoreOptionClick(option)}
              items={finalizedOptions}
            />
          </Grid>
        </Grid>
      </CardWrapper>
      {openCloseDialog && job && (
        <CloseJobDialogue
          onClose={() => setOpenCloseDialog(false)}
          open={openCloseDialog}
          job={job}
          pageRefresh={getJobDetails}
        />
      )}
      {campaignDrawer && (
        <CampaignEmailDrawer
          open={campaignDrawer}
          onClose={handleToggleDrawer}
          campaignEmailCandidateData={finalizedCampaignData}
        />
      )}
    </>
  );
};

export default JobCRMHeader;
